import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {useCreateCustomFieldPropertyMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface CustomFieldPropertyUpdateFormProps extends RequiredTestIdProps {
  customFieldDefinitionId: string;
}

const schema = z.object({
  customFieldProperty: z
    .string()
    .nonempty()
    .refine((value) => !/\s/.test(value), {
      message: i18n.t('general.errors.string.whiteSpace'),
    }),
});

type FormValues = z.infer<typeof schema>;

export function CustomFieldPropertyUpdateForm(props: CustomFieldPropertyUpdateFormProps) {
  const [createCustomFieldProperty] = useCreateCustomFieldPropertyMutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    await createCustomFieldProperty({
      customFieldDefinitionId: props.customFieldDefinitionId,
      createCustomFieldPropertyRequestBody: {propertyName: values.customFieldProperty},
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<FormValues> onSubmit={handleSubmit} experimentalZodSchema={schema}>
      {(control) => (
        <VStack spacing={4}>
          <FormField
            type="text"
            control={control}
            name="customFieldProperty"
            label={i18n.t('page.generalSettings.actions.updateTemplateValue')}
            data-testid={suffixTestId('customFieldProperty', props)}
            isRequired
          />

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discardChanges')}
              variant="secondary"
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('discardChanges', props)}
            />
            <FormButton
              leftIcon="content/save"
              title={i18n.t('general.actions.saveChanges')}
              control={control}
              data-testid={suffixTestId('save', props)}
              type="submit"
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
